import { isValidCIF, isValidDNI, isValidNIE } from '@nemuru-eng/js-api-client';
import { z } from 'zod';

import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import {
  optionalDialCode,
  phoneValidation,
  requiredEmail,
  requiredPastDate,
  requiredPhoneNumber,
  requiredString,
} from '@/utils/zodUtils';

type ContactInformationFormOptions = {
  isOrderReference1Required: boolean;
  isDocumentNumberRequired: boolean;
  isBirthDateRequired: boolean;
};

export const ContactInformationFormBaseSchema = (t: GetTextIdTranslationType, options: ContactInformationFormOptions) =>
  z.object({
    firstName: requiredString(t),
    lastName: requiredString(t),
    documentNumber: options.isDocumentNumberRequired ? requiredString(t) : z.string().optional(),
    email: requiredEmail(t),
    dialCode: optionalDialCode,
    phoneNumber: requiredPhoneNumber(t),
    ...(options.isBirthDateRequired ? { birthDate: requiredPastDate(t) } : {}),
    orderReference1: options.isOrderReference1Required
      ? z
          .string()
          .regex(/^[a-zA-Z0-9\\/\-_+@\s]*$/, t('REFERENCE_CHARACTER_NOT_ALLOWED'))
          .trim()
      : z
          .string()
          .regex(/^[a-zA-Z0-9\\/\-_+@\s]*$/, t('REFERENCE_CHARACTER_NOT_ALLOWED'))
          .trim()
          .optional(),
    orderReference2: z
      .string()
      .regex(/^[a-zA-Z0-9\\/\-_+@\s]*$/, t('REFERENCE_CHARACTER_NOT_ALLOWED'))
      .trim()
      .optional(),
  });

export const addContactInformationFormRefine = (BaseSchema: z.AnyZodObject, t: GetTextIdTranslationType) =>
  BaseSchema.refine((data) => !(data.phoneNumber && data.dialCode && !phoneValidation(data)), {
    message: t('INVALID_PHONE_NUMBER'),
    path: ['phoneNumber'],
  })
    .refine((data) => !(data.phoneNumber && !data.dialCode), {
      message: t('INVALID_DIAL_CODE'),
      path: ['dialCode'],
    })
    .refine(
      (data) =>
        !(
          data.documentNumber &&
          !(isValidDNI(data.documentNumber) || isValidNIE(data.documentNumber) || isValidCIF(data.documentNumber))
        ),
      {
        message: t('INVALID_DOCUMENT_NUMBER'),
        path: ['documentNumber'],
      },
    );

export type ContactInformationFormType = z.infer<ReturnType<typeof ContactInformationFormBaseSchema>>;
